import { useMemo } from 'react';
import { useBusinessIdea } from './useBusinessIdea';
import { useAuth } from '@/lib';
import { getAllTasks, getTaskStage, getTaskModule } from '@orbit/shared/content-hierarchy';
import { Stage } from '@orbit/shared/constants';
import { BusinessIdea } from '@orbit/shared/types';

interface TaskProgress {
  taskId: string;
  title: string;
  stage: string;
  module: string;
  isCompleted: boolean;
  individualCompletionRequired: boolean;
}

interface StageProgress {
  stage: Stage;
  totalTasks: number;
  completedTasks: number;
  completionPercentage: number;
  completedTaskTitles: string[];
  remainingTaskTitles: string[];
}

export const useTaskProgress = () => {
  const { businessIdea, loading } = useBusinessIdea();
  const { user } = useAuth();

  const taskProgress = useMemo(() => {
    if (!businessIdea || !user) {
      return {
        allTasks: [],
        stageProgress: {},
        loading: true,
      };
    }

    const completedTaskIds = (businessIdea as any).completedTaskIds || [];
    const taskProgressData = (businessIdea as any).taskProgress || {};
    const allTasks = getAllTasks();

    // Process all tasks with their completion status
    const processedTasks: TaskProgress[] = allTasks.map((task) => {
      const isCompleted = completedTaskIds.includes(task.id);
      const taskProgressRecord = taskProgressData[task.id];
      const userTaskProgress = taskProgressRecord?.[user.id];

      // Check individual completion requirements
      let finalIsCompleted = isCompleted;
      if (task.individualCompletionRequired) {
        const userCompleted = userTaskProgress && userTaskProgress.status === 'completed';
        finalIsCompleted = userCompleted;
      }

      return {
        taskId: task.id,
        title: task.title,
        stage: getTaskStage(task.id),
        module: getTaskModule(task.id),
        isCompleted: finalIsCompleted,
        individualCompletionRequired: task.individualCompletionRequired || false,
      };
    });

    // Calculate stage progress
    const stageProgress: Record<Stage, StageProgress> = {
      [Stage.Explore]: {
        stage: Stage.Explore,
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        completedTaskTitles: [],
        remainingTaskTitles: [],
      },
      [Stage.Incubation]: {
        stage: Stage.Incubation,
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        completedTaskTitles: [],
        remainingTaskTitles: [],
      },
      [Stage.Launch]: {
        stage: Stage.Launch,
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        completedTaskTitles: [],
        remainingTaskTitles: [],
      },
      [Stage.Growth]: {
        stage: Stage.Growth,
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        completedTaskTitles: [],
        remainingTaskTitles: [],
      },
    };

    // Group tasks by stage and calculate progress
    processedTasks.forEach((task) => {
      const stageKey = task.stage as Stage;
      if (stageProgress[stageKey]) {
        stageProgress[stageKey].totalTasks++;

        if (task.isCompleted) {
          stageProgress[stageKey].completedTasks++;
          stageProgress[stageKey].completedTaskTitles.push(task.title);
        } else {
          stageProgress[stageKey].remainingTaskTitles.push(task.title);
        }
      }
    });

    // Calculate completion percentages
    Object.values(stageProgress).forEach((stage) => {
      stage.completionPercentage =
        stage.totalTasks > 0 ? Math.round((stage.completedTasks / stage.totalTasks) * 100) : 0;
    });

    return {
      allTasks: processedTasks,
      stageProgress,
      loading: false,
    };
  }, [businessIdea, user]);

  return {
    ...taskProgress,
    loading: loading || taskProgress.loading,
  };
};
